import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/common/Button';
import { usePageTitle } from '../hooks/usePageTitle';
import type { SessionExpiredState } from '../types/errors';

/**
 * US10 - Session Expired Page (FR-110 to FR-114)
 *
 * Displays a user-friendly message when their session has expired.
 * Provides a "Log In Again" button that preserves the original return URL.
 */
export function SessionExpiredPage() {
  usePageTitle('Session Expired — Hawkes Bridge');
  const navigate = useNavigate();
  const location = useLocation();
  const [returnUrl, setReturnUrl] = useState('/dashboard');

  // Get the return URL from router state or sessionStorage
  useEffect(() => {
    // First try router state
    const routerState = location.state as SessionExpiredState | undefined;
    if (routerState?.returnUrl) {
      setReturnUrl(routerState.returnUrl);
      return;
    }

    // Fall back to sessionStorage (used by window.location redirects)
    const storedState = sessionStorage.getItem('redirectState');
    if (storedState) {
      try {
        const parsed = JSON.parse(storedState) as SessionExpiredState;
        if (parsed.returnUrl) {
          setReturnUrl(parsed.returnUrl);
        }
      } catch {
        // Ignore parse errors
      }
      // Clean up stored state
      sessionStorage.removeItem('redirectState');
    }
  }, [location.state]);

  const handleLoginClick = () => {
    // Navigate to login page with returnUrl in state
    navigate('/login', { state: { returnUrl } });
  };

  const handleHomeClick = () => {
    navigate('/');
  };

  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="max-w-md w-full mx-4">
        <div className="bg-card rounded-lg shadow-lg p-8 text-center">
          {/* Icon */}
          <div className="mx-auto w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-6">
            <svg
              className="w-8 h-8 text-amber-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>

          {/* Heading */}
          <h1 className="text-2xl font-bold text-ink mb-2">Session Expired</h1>

          {/* Message - FR-111, FR-173 */}
          <p className="text-ink-soft mb-6">
            Your session has expired. Please log in again to continue.
          </p>

          {/* Actions */}
          <div className="space-y-3">
            {/* Primary action - FR-112 */}
            <Button
              onClick={handleLoginClick}
              variant="primary"
              className="w-full"
              data-testid="login-again-button"
            >
              Log In Again
            </Button>

            {/* Secondary action */}
            <Button
              onClick={handleHomeClick}
              variant="secondary"
              className="w-full"
              data-testid="go-home-button"
            >
              Go to Home Page
            </Button>
          </div>

          {/* Help text - FR-173: Generic message covering both JWT and inactivity expiration */}
          <p className="text-sm text-ink-soft mt-6">
            For security, sessions expire automatically. Simply log in again to continue.
          </p>
        </div>
      </div>
    </div>
  );
}
